import type {NfServiceWorkerCommunication} from "@/services/NfServiceWorkerCommunication";
import {SetLanguage} from "@/serviceWorker/workerComponents/MessageTypes/SetLanguage";
import {
    GetBookingEngineConfiguration
} from "@/serviceWorker/workerComponents/MessageTypes/GetBookingEngineConfiguration";
import {GetStyles} from "@/serviceWorker/workerComponents/MessageTypes/GetStyles";
import {
    GetHotelTranslatedConfiguration
} from "@/serviceWorker/workerComponents/MessageTypes/GetHotelTranslatedConfiguration";
import {GetTranslations} from "@/serviceWorker/workerComponents/MessageTypes/GetTranslations";
import {NfMessage, NfMessageType} from "@/serviceWorker/workerComponents/NfMessage";
import {
    GetBookingEngineConfigurationResult
} from "@/serviceWorker/workerComponents/MessageTypes/GetBookingEngineConfigurationResult";
import {GetStylesResult} from "@/serviceWorker/workerComponents/MessageTypes/GetStylesResult";
import {
    GetHotelTranslatedConfigurationResult
} from "@/serviceWorker/workerComponents/MessageTypes/GetHotelTranslatedConfigurationResult";
import {GetTranslationsResult} from "@/serviceWorker/workerComponents/MessageTypes/GetTranslationsResult";
import {Deferred} from "@/serviceWorker/workerComponents/Utilities/Deferred";
import type {BookingEngineConfigModel} from "@/serviceWorker/workerComponents/Models/BookingEngineConfigModel";
import type {
    HotelTranslatedConfigurationModel
} from "@/serviceWorker/workerComponents/Models/HotelTranslatedConfigurationModel";
import {loadTranslations, resetTranslations} from "@/utilities/translations";
import type {LanguageData} from "@/frontendClasses/LanguageData";
import {ReloadBookingEngine} from "@/serviceWorker/workerComponents/MessageTypes/ReloadBookingEngine";
import {getEnv} from "@/utilities/env";
import {ref, type Ref} from "vue";

export class NfConfigService {
    private communication: NfServiceWorkerCommunication;
    private widgetId: number;
    public availableLanguages: Record<string,LanguageData> = {
        pl: {
            id:1,
            label: 'Polski',
            code: 'pl'
        },
        en: {
            id:3,
            label: 'English',
            code: 'en'
        },
        de: {
            id:2,
            label: 'Deutsch',
            code: 'de'
        },
        ru: {
            id:24,
            label: 'Pусский',
            code: 'ru'
        },
        cs: {
            id: 47,
            label: 'Čeština',
            code: 'cs'
        },
        it: {
            id: 41,
            label: 'Italiano',
            code: 'it'
        },
        fr: {
            id: 25,
            label: 'Français',
            code: 'fr'
        },
        es: {
            id: 43,
            label: 'Español',
            code: 'es'
        },
        uk: {
            id: 26,
            label: 'Українська',
            code: 'uk'
        },
    };
    public currentLanguage = this.availableLanguages.pl;
    /**
     * True, when configuration, translatedConfiguration, styles and translations are loaded
     */
    public ready: Ref<boolean> = ref(false);
    private configurationReadyDeferred = new Deferred<void>();
    /**
     * Resolved, when configuration, translatedConfiguration, styles and translations are loaded
     */
    public configurationReadyPromise = this.configurationReadyDeferred.promise;
    public constructor( communication: NfServiceWorkerCommunication, widgetId: number, requestedLanguageCode?: string) {
        this.widgetId = widgetId;
        this.communication = communication;
        this.ready.value = false;
        this.loadLanguage(requestedLanguageCode);
        this.communication.communicationReadyPromise.then(this.onCommunicationReady.bind(this));
    }

    private checkIfReady() {
        if (this.configuration && this.translatedConfigurationDeferred.isResolved() && this.stylesDeferred.isResolved() && this.translationsDeferred.isResolved()
            && (window.moment?.locales().includes(this.currentLanguage?.code || '') || this.momentLanguageLoadedDefer.isResolved())) {
            this.ready.value = true;
            this.configurationReadyDeferred.resolve();
        }
    }
    private onCommunicationReady() {
        this.communication.sendMessage(new SetLanguage(this.currentLanguage.id,this.currentLanguage.code, this.currentLanguage.label));
        //region czekamy na ustawienia
        this.communication.getNextMessagePromise(NfMessageType.GetBookingEngineConfigurationResult).then(this.onConfigurationLoaded.bind(this));
        this.communication.getNextMessagePromise(NfMessageType.GetStylesResult).then(this.onStylesLoaded.bind(this));
        this.communication.getNextMessagePromise(NfMessageType.GetHotelTranslatedConfigurationResult).then(this.onTranslatedConfigurationLoaded.bind(this));
        this.communication.getNextMessagePromise(NfMessageType.GetTranslationsResult).then(this.onTranslationsLoaded.bind(this));
        //endregion

        //region wysyłamy prośby o ustawienia
        this.communication.sendMessage(new GetBookingEngineConfiguration(this.widgetId));
        this.communication.sendMessage(new GetStyles(this.widgetId));
        this.communication.sendMessage(new GetHotelTranslatedConfiguration(this.widgetId, this.currentLanguage.id));
        this.communication.sendMessage(new GetTranslations(this.widgetId, this.currentLanguage.id));
        //endregion
    }

    private loadLanguage(requestedLanguageCode?: string) {
        if(window.localStorage.getItem('NfBookingEngine.languageCode')) {
            requestedLanguageCode = window.localStorage.getItem('NfBookingEngine.languageCode') as string;
        } else if(requestedLanguageCode) {
            //region jeśli zostaliśmy poproszeni o konkretny język, to sprawdzamy czy jest dostępny
            requestedLanguageCode = requestedLanguageCode.trim().substring(0, 2).toLowerCase();
            if(!(requestedLanguageCode in this.availableLanguages)) {
                requestedLanguageCode = undefined;
            }
            //endregion
        }
        if(!requestedLanguageCode) {
            //region jeśli nie zostaliśmy poproszeni o konkretny język, to sprawdzamy czy przeglądarka ma jakiś wybrany
            if (window.navigator) {
                //@ts-ignore
                const navigatorLanguage = window.navigator.languages || [window.navigator.language || window.navigator.userLanguage];
                for(let languageCode of navigatorLanguage) {
                    if(!languageCode) {
                        continue;
                    }
                    languageCode = languageCode.trim().substring(0, 2).toLowerCase();
                    if(languageCode in this.availableLanguages) {
                        // @ts-ignore
                        this.currentLanguage = this.availableLanguages[languageCode];
                        this.loadMomentLanguage(languageCode);
                        return;
                    }
                }

            }
            this.currentLanguage = this.availableLanguages.en;
            requestedLanguageCode = 'en';
            //endregion
        } else {
            // @ts-ignore
            this.currentLanguage = this.availableLanguages[requestedLanguageCode];
        }
        this.loadMomentLanguage(requestedLanguageCode);
    }
    public chooseLanguage(languageCode: string): void {
        languageCode = languageCode.toLowerCase().trim();
        if(this.availableLanguages[languageCode]) {
            window.localStorage.setItem('NfBookingEngine.languageCode',languageCode);
            resetTranslations();
            this.communication.sendMessage(new SetLanguage(this.availableLanguages[languageCode].id,
                this.availableLanguages[languageCode].code, this.availableLanguages[languageCode].label));
            window.setTimeout(() => {
                this.communication.sendMessage(new ReloadBookingEngine(true));
            })
        }
    }
    private momentLanguageLoadedDefer = new Deferred();
    private loadMomentLanguage(languageCode: string): void {
        if(languageCode == self.moment.locale()) {
            //console.debug('Tłumaczenia moment załadowane', languageCode);
            this.momentLanguageLoadedDefer.resolve();
            this.checkIfReady();
            return;
        }
        if(self.moment.locales().includes(languageCode)) {
            self.moment.locale(languageCode);
            this.momentLanguageLoadedDefer.resolve();
            this.checkIfReady();
            return;
        }
        //@ts-ignore
        if (!window['moment-locale-' + languageCode + '-script-loading']) {
            //@ts-ignore
            window['moment-locale-' + languageCode + '-script-loading'] = true;

            const momentLocaleUrl = (getEnv('VITE_MOMENT_LOCALE_URL')).replace('lang_code',languageCode);
            fetch(momentLocaleUrl).then(async (response) => {
                if (!response.ok) {
                    throw Error(`Nie udało się pobrać pliku z tłumaczeniem moment dla języka ${languageCode}`);
                }
                const script = await response.text();
                //Specjalny sposób ładowania skryptu, żeby zignorować UMD, AMD i CommonJS
                (new Function('const define = null; const require = null; ' + script )).apply(self);
                self.moment.locale(languageCode);
                this.momentLanguageLoadedDefer.resolve();
                this.checkIfReady();
            });
        } else {
            const waitingForMomentLanguageInterval = window.setInterval(() => {
                if(!window.moment.locales().includes(languageCode)) {
                    return;
                } else {
                    self.moment.locale(languageCode);
                }
                if(window.moment([2000,0,1]).format('MMMM') != 'January') {
                    //console.debug('Tłumaczenia moment załadowane', languageCode);
                    clearInterval(waitingForMomentLanguageInterval);
                    //@ts-ignore
                    this.momentLanguageLoadedDefer.resolve();
                    this.checkIfReady();
                }
            },5);
        }
    }

    private configuration?: BookingEngineConfigModel;
    private configurationDeferred = new Deferred<BookingEngineConfigModel>();
    public configurationPromise = this.configurationDeferred.promise;
    private onConfigurationLoaded(configurationResult: NfMessage) {
        if(!(configurationResult instanceof GetBookingEngineConfigurationResult)) {
            throw Error('Zła odpowiedź, powinna być typu GetBookingEngineConfigurationResult');
        }
        if(configurationResult.widgetId != this.widgetId) {
            return;
        }
        if(!configurationResult.configuration) {
            throw Error('Pusta konfiguracja booking engine');
        }
        //console.debug('konfiguracja booking engine załadowana',configurationResult.configuration);
        this.configuration = configurationResult.configuration;
        this.configurationDeferred.resolve(configurationResult.configuration);
        this.checkIfReady();
    }

    private stylesDeferred = new Deferred<string>();
    public stylesPromise = this.stylesDeferred.promise;
    private onStylesLoaded(stylesResult: NfMessage) {
        if(!(stylesResult instanceof GetStylesResult)) {
            throw Error('Zła odpowiedź, powinna być typu GetStylesResult');
        }
        if(stylesResult.widgetId != this.widgetId) {
            return;
        }
        //console.debug('style załadowane');
        this.stylesDeferred.resolve(stylesResult.styles);
        this.checkIfReady();
    }

    private translatedConfigurationDeferred = new Deferred<HotelTranslatedConfigurationModel>();
    public translatedConfigurationPromise = this.translatedConfigurationDeferred.promise;
    private onTranslatedConfigurationLoaded(translatedConfigurationResult: NfMessage) {
        if(!(translatedConfigurationResult instanceof GetHotelTranslatedConfigurationResult)) {
            throw Error('Zła odpowiedź, powinna być typu GetHotelTranslatedConfigurationResult');
        }
        if(translatedConfigurationResult.widgetId != this.widgetId) {
            return;
        }
        if(!translatedConfigurationResult.translatedConfiguration) {
            throw Error('Pusta przetłumaczona konfiguracja booking engine');
        }
        //console.debug('przetłumaczona konfiguracja załadowana',translatedConfigurationResult.translatedConfiguration);
        this.momentLanguageLoadedDefer.promise.then(() => {
            this.translatedConfigurationDeferred.resolve(translatedConfigurationResult.translatedConfiguration);
            this.checkIfReady();
        });
    }

    private translationsDeferred = new Deferred<object>();
    private onTranslationsLoaded(translationsResult: NfMessage) {
        if(!(translationsResult instanceof GetTranslationsResult)) {
            throw Error('Zła odpowiedź, powinna być typu GetTranslationsResult');
        }
        if(translationsResult.widgetId != this.widgetId) {
            return;
        }
        if(!translationsResult.translations) {
            throw Error('Puste tłumaczenia');
        }
        loadTranslations(translationsResult.translations);
        //console.debug('tłumaczenia załadowane',translationsResult.translations);
        this.translationsDeferred.resolve(translationsResult.translations);
        this.checkIfReady();
    }
    // return image
    // 'w1200-h1200',
    // 'w140-c',
    // 'w200-c',
    // 'w245-h160-c',
    // 'w400-c',
    // 'w500-c',
    // 'h120-c',
    // 'h60-c',
    // 'w900-h550-c',
    getPhotoUrl(name: string, size: string) {
        return `${getEnv('VITE_IMAGES_HOST')}/${size}/${name}`;
    }
    getPhotoSrcSet(name:string) {
        return `${this.getPhotoUrl(name, 'w360')} 360w, ${this.getPhotoUrl(name, 'w400-c')} 400w,`
            + ` ${this.getPhotoUrl(name, 'w500-c')} 500w`;
    }/**
     * Formats a price
     * @param price price to be formatted
     * @param formattedDecimals if true, will return html with formatted decimals. DO NOT use currency pipe
     * , if you will use formattedDecimals
     * @return string with formatted price, or html
     */
    formatPrice(price: number|string, formattedDecimals = false) {
        if(typeof(price) === 'undefined' || price === null) {
            return '';
        }
        price = price.toString();
        if (!formattedDecimals) {
            price = price.replace('ABC', '').replace('.', ',');
            if(price[price.length - 2] == ',') {
                price = price +'0';
            }
            return price;
        }
        else {
            if (this.configuration?.prices_rounded == '1') {
                return this.insertThousandSeparators(parseFloat(price).toFixed(0));
            } else {
                const priceSplit = parseFloat(price).toFixed(2).split('.');
                if (priceSplit.length < 2)
                    return this.insertThousandSeparators(price);
                else {
                    return `${this.insertThousandSeparators(priceSplit[0])}<span class="decimals">,${priceSplit[1]}</span>`;
                }
            }
        }
    }


    private insertThousandSeparators(text: number|string) {
        text = text.toString().trim();
        let result = '';
        let startIndex = text.length, endIndex = text.length;
        for (let i = 3; i < text.length; i += 3) {
            startIndex = text.length - i;
            result = text.substring(startIndex, endIndex) + result;
            if (startIndex > 0)
                result = ' ' + result;
            endIndex = startIndex;
        }
        result = text.substring(0, startIndex) + result;
        return result;
    }
}
